import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, PieChart, Scale, Target, Activity, ChevronDown } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { CalculatorJsonLd } from '../components/seo/SchemaMarkup';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';
import { EmailResultsGate } from '../components/tools/EmailResultsGate';

const MACRO_SEO = {
  name: "Macro Calculator - Protein, Carbs & Fat",
  description: "Calculate your ideal macronutrient split based on your TDEE, fitness goals, and activity level. Get exact grams for protein, carbs, and fat.",
  url: "https://ninjathlete.com/tools/macro-calculator",
  steps: [
    { name: "Enter your details", text: "Provide your sex, age, height, and weight." },
    { name: "Select your goal", text: "Choose from Fat Loss, Maintain Weight, Build Muscle, Keto, or Balanced Health." },
    { name: "Set activity level", text: "Select your weekly exercise frequency." },
    { name: "Calculate macros", text: "Click Calculate to get your personalized macro split with exact grams per day for protein, carbs, and fat." },
  ],
  faqs: [
    { question: "What are macros?", answer: "Macros (macronutrients) are the three main nutrients your body needs in large amounts: protein, carbohydrates, and fat. Each serves different functions for energy, recovery, and health." },
    { question: "What macro split is best for fat loss?", answer: "A common fat loss split is 40% protein, 25% carbs, 35% fat. Higher protein preserves muscle while the calorie deficit drives fat loss." },
    { question: "Should I count macros or calories?", answer: "Tracking macros is more effective than counting calories alone because it ensures you get the right balance of nutrients for your goals, not just the right total energy." },
  ],
};

const GOALS = [
  { value: 'lose', label: 'Fat Loss', protein: 40, carbs: 25, fat: 35 },
  { value: 'maintain', label: 'Maintain Weight', protein: 30, carbs: 40, fat: 30 },
  { value: 'build', label: 'Build Muscle', protein: 35, carbs: 45, fat: 20 },
  { value: 'keto', label: 'Keto / Low-Carb', protein: 30, carbs: 5, fat: 65 },
  { value: 'balanced', label: 'Balanced Health', protein: 30, carbs: 35, fat: 35 },
];

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary', factor: 1.2 },
  { value: 'light', label: 'Light (1-3x/week)', factor: 1.375 },
  { value: 'moderate', label: 'Moderate (3-5x/week)', factor: 1.55 },
  { value: 'active', label: 'Very Active (6-7x/week)', factor: 1.725 },
  { value: 'extreme', label: 'Extreme (2x/day)', factor: 1.9 },
];

const Select = ({ label, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative" data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm hover:border-[#E63946]/50 transition-colors">
        <span>{selected?.label || 'Select...'}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl">
          {options.map(o => (
            <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-4 py-3 text-sm ${o.value === value ? 'bg-[#E63946]/20 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MacroBar = ({ label, grams, calories, percent, color }) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-1">
      <span className="text-white text-sm font-semibold">{label}</span>
      <span className="text-zinc-400 text-sm">{grams}g &bull; {calories} cal</span>
    </div>
    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${percent}%`, backgroundColor: color }} />
    </div>
    <p className="text-zinc-600 text-xs mt-1 text-right">{percent}%</p>
  </div>
);

export default function MacroCalculatorPage() {
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [unit, setUnit] = useState('imperial');
  const [goal, setGoal] = useState('maintain');
  const [activity, setActivity] = useState('moderate');
  const [meals, setMeals] = useState(4);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const calculate = () => {
    if (!age || !weight || !height) return;
    const ageNum = parseFloat(age);
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);
    if (ageNum <= 0 || weightNum <= 0 || heightNum <= 0 || isNaN(ageNum) || isNaN(weightNum) || isNaN(heightNum)) return;
    let wKg = unit === 'imperial' ? weightNum * 0.453592 : weightNum;
    let hCm = unit === 'imperial' ? heightNum * 2.54 : heightNum;
    let bmr = gender === 'male'
      ? 10 * wKg + 6.25 * hCm - 5 * ageNum + 5
      : 10 * wKg + 6.25 * hCm - 5 * ageNum - 161;
    const actData = ACTIVITY_LEVELS.find(a => a.value === activity);
    let tdee = Math.round(bmr * actData.factor);
    // Adjust for goal
    if (goal === 'lose') tdee -= 400;
    else if (goal === 'build') tdee += 300;
    const goalData = GOALS.find(g => g.value === goal);
    const protein = { grams: Math.round((tdee * goalData.protein / 100) / 4), pct: goalData.protein };
    const carbs = { grams: Math.round((tdee * goalData.carbs / 100) / 4), pct: goalData.carbs };
    const fat = { grams: Math.round((tdee * goalData.fat / 100) / 9), pct: goalData.fat };
    setResult({ tdee, protein, carbs, fat });
    setSaved(false);
  };

  return (
    <ToolSEOWrapper toolKey="macro-calculator">
    <main className="pt-20 min-h-screen" data-testid="macro-calculator-page">
      <CalculatorJsonLd {...MACRO_SEO} calculatorName={MACRO_SEO.name} />
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="macro-calc-title">Macro Calculator</h1>
            <p className="text-zinc-400">Get your ideal protein, carb, and fat split based on your body and goals</p>
          </div>
          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            <div className="mb-4">
              <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Gender</label>
              <div className="flex gap-2" data-testid="gender-toggle">
                {['male', 'female'].map(g => (
                  <button key={g} onClick={() => setGender(g)} className={`flex-1 py-3 text-sm font-heading uppercase tracking-wider transition-colors ${gender === g ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white'}`}>{g}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[
                { l: 'Age', v: age, s: setAge, p: 'Years' },
                { l: unit === 'imperial' ? 'Weight (lbs)' : 'Weight (kg)', v: weight, s: setWeight, p: 'Weight' },
                { l: unit === 'imperial' ? 'Height (in)' : 'Height (cm)', v: height, s: setHeight, p: 'Height' },
              ].map(f => (
                <div key={f.l}>
                  <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{f.l}</label>
                  <input type="number" value={f.v} onChange={e => f.s(e.target.value)} placeholder={f.p}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors" />
                </div>
              ))}
              <Select label="Units" value={unit} options={[{ value: 'imperial', label: 'Imperial' }, { value: 'metric', label: 'Metric' }]} onChange={setUnit} testId="unit-select" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Select label="Goal" value={goal} options={GOALS} onChange={setGoal} testId="goal-select" />
              <Select label="Activity Level" value={activity} options={ACTIVITY_LEVELS} onChange={setActivity} testId="activity-select" />
            </div>
            <div className="mb-6">
              <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Meals Per Day</label>
              <div className="flex gap-2" data-testid="meals-toggle">
                {[3, 4, 5, 6].map(n => (
                  <button key={n} onClick={() => setMeals(n)}
                    className={`flex-1 py-3 text-sm font-heading uppercase tracking-wider transition-colors ${meals === n ? 'bg-[#00F0FF] text-black' : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white'}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={calculate} disabled={!age || !weight || !height}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-macros-btn">
              Calculate My Macros
            </button>
          </div>

          {result && (
            <div className="mt-8" data-testid="macro-results">
              <EmailResultsGate
                toolId="macro-calculator"
                toolName="Macro Plan"
                resultPayload={{
                  tdee: result.tdee,
                  protein_g: result.protein.grams,
                  carbs_g: result.carbs.grams,
                  fat_g: result.fat.grams,
                  goal: GOALS.find(g => g.value === goal)?.label,
                  meals_per_day: meals,
                }}
              >
                <div className="animate-entrance">
                  <div className="bg-zinc-900/80 border border-[#E63946]/30 p-6 md:p-8 text-center mb-6">
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Daily Calorie Target</p>
                    <p className="text-5xl font-heading text-white" data-testid="macro-tdee">{result.tdee}</p>
                    <p className="text-zinc-600 text-xs">calories/day</p>
                  </div>
                  <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
                    <h3 className="text-white text-lg font-heading mb-6 flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-[#00F0FF]" /> Your Macro Split
                    </h3>
                    <MacroBar label="Protein" grams={result.protein.grams} calories={result.protein.grams * 4} percent={result.protein.pct} color="#E63946" />
                    <MacroBar label="Carbohydrates" grams={result.carbs.grams} calories={result.carbs.grams * 4} percent={result.carbs.pct} color="#00F0FF" />
                    <MacroBar label="Fat" grams={result.fat.grams} calories={result.fat.grams * 9} percent={result.fat.pct} color="#FFD100" />
                  </div>

                  {/* Per-Meal Breakdown */}
                  <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8 mt-4">
                    <h3 className="text-white text-lg font-heading mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#00F0FF]" /> Per-Meal Breakdown ({meals} meals/day)
                    </h3>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { l: 'Calories', v: Math.round(result.tdee / meals), u: 'cal', c: 'text-white' },
                        { l: 'Protein', v: Math.round(result.protein.grams / meals), u: 'g', c: 'text-[#E63946]' },
                        { l: 'Carbs', v: Math.round(result.carbs.grams / meals), u: 'g', c: 'text-[#00F0FF]' },
                        { l: 'Fat', v: Math.round(result.fat.grams / meals), u: 'g', c: 'text-[#FFD100]' },
                      ].map(m => (
                        <div key={m.l} className="text-center bg-zinc-800 border border-zinc-700 p-3">
                          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">{m.l}</p>
                          <p className={`text-xl font-heading ${m.c}`}>{m.v}<span className="text-xs text-zinc-500 ml-0.5">{m.u}</span></p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6">
                    <SaveResultButton
                      saved={saved}
                      onSave={() => {
                        saveResult('macros', {
                          value: result.tdee,
                          label: `${result.tdee} cal | P:${result.protein.grams}g C:${result.carbs.grams}g F:${result.fat.grams}g`,
                          tdee: result.tdee,
                          protein: result.protein.grams, carbs: result.carbs.grams, fat: result.fat.grams,
                          goal: GOALS.find(g => g.value === goal)?.label,
                        });
                        setSaved(true);
                      }}
                    />
                  </div>
                </div>
              </EmailResultsGate>
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
